import {
  channelsFromAxes,
  DEFAULT_INPUT_PROFILE,
  type AuxBinding,
  type AuxChannel,
  type InputProfile,
  type LogicalAxis,
  type ResolvedChannels,
} from './channels';
import type { AxisCurveConfig } from './curve';
import type { StoredInputProfile } from './profiles';

export const CALIBRATION_STEPS = [
  'detect-center',
  'sweep-extremes',
  'assign-channels',
  'tune-curves',
  'assign-aux',
  'review',
] as const;

export type CalibrationStep = (typeof CALIBRATION_STEPS)[number];
export type ChannelAssignments = Partial<Record<LogicalAxis, number>>;

export interface CalibrationFrame {
  axes: number[];
  buttons: number[];
}

export interface CalibrationWizardOptions {
  deviceHash: string;
  deviceName: string;
  axisCount: number;
  buttonCount: number;
}

export interface CalibrationAxisState {
  index: number;
  min: number;
  max: number;
  center: number;
  captured: boolean;
}

export interface CalibrationScreenState {
  step: CalibrationStep;
  perAxis: CalibrationAxisState[];
  channelAssignments: ChannelAssignments;
  previewAt(rawAxes: readonly number[]): ResolvedChannels;
  canSave: boolean;
  blockedReason?: string;
  /** What's stopping "Next" from advancing past the current step, if anything. */
  nextStepBlockedReason?: string;
}

interface AxisCapture {
  centerSum: number;
  centerSamples: number;
  center?: number;
  min?: number;
  max?: number;
}

const CHANNELS: readonly LogicalAxis[] = ['roll', 'pitch', 'yaw', 'throttle'];
const cloneAuxBinding = (binding: AuxBinding): AuxBinding => ({ ...binding });
const defaultCurve = (): AxisCurveConfig => ({
  min: -1,
  max: 1,
  center: 0,
  deadzonePct: 0,
  reverse: false,
  expo: 0,
});

const captureIsComplete = (capture: AxisCapture): boolean =>
  capture.center !== undefined &&
  capture.min !== undefined &&
  capture.max !== undefined &&
  capture.min < capture.center &&
  capture.center < capture.max;

const assertCurve = (curve: AxisCurveConfig): void => {
  if (
    !Number.isFinite(curve.min) ||
    !Number.isFinite(curve.max) ||
    !Number.isFinite(curve.center) ||
    curve.min >= curve.center ||
    curve.center >= curve.max
  ) {
    throw new RangeError('axis curve requires finite min < center < max');
  }
  if (curve.deadzonePct < 0 || curve.deadzonePct > 1 || !Number.isFinite(curve.deadzonePct)) {
    throw new RangeError('axis deadzonePct must be in [0, 1]');
  }
  if (curve.expo < 0 || curve.expo > 1 || !Number.isFinite(curve.expo)) {
    throw new RangeError('axis expo must be in [0, 1]');
  }
};

/** Pure calibration state machine. All time and input samples are supplied by the caller. */
export class CalibrationWizard {
  private stepIndex = 0;
  private readonly captures: AxisCapture[];
  private readonly assignments: ChannelAssignments = {};
  private readonly curves: Record<LogicalAxis, AxisCurveConfig> = {
    roll: defaultCurve(),
    pitch: defaultCurve(),
    yaw: defaultCurve(),
    throttle: defaultCurve(),
  };
  private readonly aux = {
    arm: cloneAuxBinding(DEFAULT_INPUT_PROFILE.auxMap.arm),
    modeToggle: cloneAuxBinding(DEFAULT_INPUT_PROFILE.auxMap.modeToggle),
    reset: cloneAuxBinding(DEFAULT_INPUT_PROFILE.auxMap.reset),
    releaseManual: cloneAuxBinding(DEFAULT_INPUT_PROFILE.auxMap.releaseManual),
  };
  private lastIngestAtMs = 0;

  constructor(private readonly options: CalibrationWizardOptions) {
    if (!Number.isInteger(options.axisCount) || options.axisCount < 0) {
      throw new RangeError('axisCount must be a non-negative integer');
    }
    if (!Number.isInteger(options.buttonCount) || options.buttonCount < 0) {
      throw new RangeError('buttonCount must be a non-negative integer');
    }
    if (options.deviceHash.length === 0 || options.deviceName.length === 0) {
      throw new Error('deviceHash and deviceName are required');
    }
    this.captures = Array.from({ length: options.axisCount }, () => ({
      centerSum: 0,
      centerSamples: 0,
    }));
  }

  ingest(frame: CalibrationFrame, atMs: number): void {
    if (!Number.isFinite(atMs)) throw new RangeError('atMs must be finite');
    this.lastIngestAtMs = atMs;
    if (this.currentStep() === 'detect-center') {
      this.captures.forEach((capture, index) => {
        const value = frame.axes[index];
        if (value === undefined || !Number.isFinite(value)) return;
        capture.centerSum += value;
        capture.centerSamples += 1;
        capture.center = capture.centerSum / capture.centerSamples;
      });
    } else if (this.currentStep() === 'sweep-extremes') {
      this.captures.forEach((capture, index) => {
        const value = frame.axes[index];
        if (value === undefined || !Number.isFinite(value) || capture.center === undefined) return;
        capture.min = Math.min(capture.min ?? capture.center, value);
        capture.max = Math.max(capture.max ?? capture.center, value);
      });
      CHANNELS.forEach((channel) => {
        const axisIndex = this.assignments[channel];
        if (axisIndex !== undefined) this.applyCaptureToCurve(channel, axisIndex);
      });
    }
  }

  /** Returns an explanatory string when movement to the next step is refused. */
  advance(): string | undefined {
    const reason = this.advanceBlockedReason();
    if (reason !== undefined) return reason;
    if (this.currentStep() === 'detect-center') {
      this.captures.forEach((capture) => {
        capture.min = capture.center;
        capture.max = capture.center;
      });
    }
    this.stepIndex += 1;
    return undefined;
  }

  back(): void {
    this.stepIndex = Math.max(0, this.stepIndex - 1);
  }

  setChannelAxis(channel: LogicalAxis, axisIndex: number): void {
    this.assertAxisIndex(axisIndex);
    this.assignments[channel] = axisIndex;
    this.applyCaptureToCurve(channel, axisIndex);
  }

  autoAssignChannels(): ChannelAssignments {
    const ranked = this.captures
      .map((capture, index) => ({ capture, index }))
      .filter(({ capture }) => captureIsComplete(capture))
      .sort((left, right) => {
        const leftRange = left.capture.max! - left.capture.min!;
        const rightRange = right.capture.max! - right.capture.min!;
        return rightRange - leftRange || left.index - right.index;
      });

    CHANNELS.forEach((channel, channelIndex) => {
      const selected = ranked[channelIndex];
      if (selected === undefined) {
        delete this.assignments[channel];
        return;
      }
      this.assignments[channel] = selected.index;
      this.applyCaptureToCurve(channel, selected.index);
    });
    return { ...this.assignments };
  }

  setCurve(channel: LogicalAxis, update: Partial<AxisCurveConfig>): void {
    const next = { ...this.curves[channel], ...update };
    assertCurve(next);
    this.curves[channel] = next;
  }

  setAux(name: AuxChannel, binding: AuxBinding): void {
    const limit = binding.type === 'axis' ? this.options.axisCount : this.options.buttonCount;
    if (!Number.isInteger(binding.index) || binding.index < 0 || binding.index >= limit) {
      throw new RangeError(`${binding.type} aux index is outside the connected device range`);
    }
    if (!Number.isFinite(binding.onThreshold)) {
      throw new RangeError('aux onThreshold must be finite');
    }
    this.aux[name] = { ...binding };
  }

  screenState(): CalibrationScreenState {
    const blockedReason = this.saveBlockedReason();
    const nextStepBlockedReason = this.advanceBlockedReason();
    return {
      step: this.currentStep(),
      perAxis: this.captures.map((capture, index) => ({
        index,
        center: capture.center ?? 0,
        min: capture.min ?? capture.center ?? 0,
        max: capture.max ?? capture.center ?? 0,
        captured: captureIsComplete(capture),
      })),
      channelAssignments: { ...this.assignments },
      previewAt: (rawAxes) => channelsFromAxes(rawAxes, [], this.previewProfile()),
      canSave: blockedReason === undefined,
      ...(blockedReason === undefined ? {} : { blockedReason }),
      ...(nextStepBlockedReason === undefined ? {} : { nextStepBlockedReason }),
    };
  }

  buildStoredProfile(): StoredInputProfile {
    const blockedReason = this.saveBlockedReason();
    if (blockedReason !== undefined) {
      throw new Error(`Cannot save calibration: ${blockedReason}`);
    }
    return {
      version: 1,
      deviceHash: this.options.deviceHash,
      deviceName: this.options.deviceName,
      createdAt: this.lastIngestAtMs,
      profile: this.completeProfile(),
    };
  }

  private currentStep(): CalibrationStep {
    return CALIBRATION_STEPS[this.stepIndex]!;
  }

  private advanceBlockedReason(): string | undefined {
    switch (this.currentStep()) {
      case 'detect-center':
        return this.captures.filter((capture) => capture.centerSamples > 0).length >=
          CHANNELS.length
          ? undefined
          : 'Capture the center position for at least four axes.';
      case 'sweep-extremes':
        return this.captures.filter(captureIsComplete).length >= CHANNELS.length
          ? undefined
          : 'Sweep at least four axes below and above their captured centers.';
      case 'assign-channels':
        return this.assignmentBlockedReason();
      case 'tune-curves':
      case 'assign-aux':
        return undefined;
      case 'review':
        return 'Already at the review step.';
    }
  }

  private assignmentBlockedReason(): string | undefined {
    const indices = CHANNELS.map((channel) => this.assignments[channel]);
    if (indices.some((index) => index === undefined)) {
      return 'Assign an axis to roll, pitch, yaw, and throttle.';
    }
    if (new Set(indices).size !== CHANNELS.length) {
      return 'Each primary channel must use a different axis.';
    }
    const missingCenter = indices.find((index) => this.captures[index!]!.center === undefined);
    if (missingCenter !== undefined) {
      return `Axis ${missingCenter} still needs its center captured.`;
    }
    const incomplete = indices.find((index) => !captureIsComplete(this.captures[index!]!));
    return incomplete === undefined
      ? undefined
      : `Axis ${incomplete} still needs both extremes captured.`;
  }

  private saveBlockedReason(): string | undefined {
    const assignedCount = CHANNELS.filter(
      (channel) => this.assignments[channel] !== undefined,
    ).length;
    if (
      assignedCount === 0 &&
      this.captures.filter((capture) => capture.centerSamples > 0).length < CHANNELS.length
    ) {
      return 'Hold the controls at center and capture at least four axes.';
    }
    return this.assignmentBlockedReason();
  }

  private assertAxisIndex(axisIndex: number): void {
    if (!Number.isInteger(axisIndex) || axisIndex < 0 || axisIndex >= this.options.axisCount) {
      throw new RangeError('axis index is outside the connected device range');
    }
  }

  private applyCaptureToCurve(channel: LogicalAxis, axisIndex: number): void {
    const capture = this.captures[axisIndex]!;
    if (!captureIsComplete(capture)) return;
    this.curves[channel] = {
      ...this.curves[channel],
      min: capture.min!,
      max: capture.max!,
      center: capture.center!,
    };
  }

  private previewProfile(): InputProfile {
    const fallbackIndices: Record<LogicalAxis, number> = {
      roll: 0,
      pitch: Math.min(1, Math.max(0, this.options.axisCount - 1)),
      yaw: Math.min(2, Math.max(0, this.options.axisCount - 1)),
      throttle: Math.min(3, Math.max(0, this.options.axisCount - 1)),
    };
    const previewBinding = (channel: LogicalAxis) => {
      const index = this.assignments[channel] ?? fallbackIndices[channel];
      const capture = this.captures[index];
      return {
        index,
        curve:
          capture !== undefined && captureIsComplete(capture)
            ? { ...this.curves[channel] }
            : defaultCurve(),
      };
    };
    return {
      axisMap: {
        roll: previewBinding('roll'),
        pitch: previewBinding('pitch'),
        yaw: previewBinding('yaw'),
        throttle: previewBinding('throttle'),
      },
      auxMap: {
        arm: cloneAuxBinding(this.aux.arm),
        modeToggle: cloneAuxBinding(this.aux.modeToggle),
        reset: cloneAuxBinding(this.aux.reset),
        releaseManual: cloneAuxBinding(this.aux.releaseManual),
      },
    };
  }

  private completeProfile(): InputProfile {
    const axisBinding = (channel: LogicalAxis) => ({
      index: this.assignments[channel]!,
      curve: { ...this.curves[channel] },
    });
    return {
      axisMap: {
        roll: axisBinding('roll'),
        pitch: axisBinding('pitch'),
        yaw: axisBinding('yaw'),
        throttle: axisBinding('throttle'),
      },
      auxMap: {
        arm: cloneAuxBinding(this.aux.arm),
        modeToggle: cloneAuxBinding(this.aux.modeToggle),
        reset: cloneAuxBinding(this.aux.reset),
        releaseManual: cloneAuxBinding(this.aux.releaseManual),
      },
    };
  }
}
